import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  ActionIcon,
  Popover,
  RadioGroup,
  Radio,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { cobalt_blue } from "../../const/variables";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";

interface EditableTextWithOptionsProps {
  label: string;
  value: string;
  color?: string;
  options?: string[];
  onChange: (value: string) => void;
  width?: string;
  onClick?: () => void;
  isCod?: boolean;
  required?: boolean;
  readOnly?: boolean;
  customerCheck: any;
  shipping?: string;
  disabledOptions?: string[];
}

const isCodOption = (opt: string) => {
  if (!opt) return false;
  const normalized = opt.toLowerCase().trim();
  return (
    normalized.includes("giao hàng thu tiền") ||
    normalized.includes("thu tiền(cod)") ||
    normalized.includes("thu tiền (cod)")
  );
};

const SelectPaymentMethod: React.FC<EditableTextWithOptionsProps> = ({
  label,
  value,
  options = [],
  onChange,
  color,
  width = "auto",
  onClick,
  isCod = false,
  required = false,
  readOnly = false,
  customerCheck,
  shipping,
  disabledOptions,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value);
  const [searchTerm, setSearchTerm] = useState(""); // State để lưu giá trị tìm kiếm

  const isPickupAtStore = shipping === "Nhận tại cửa hàng";

  // Tự động chuyển PT thanh toán về "Thu Tiền Ngay" nếu đang là "Giao Hàng Thu Tiền(COD)" khi nhận tại cửa hàng
  useEffect(() => {
    if (!readOnly && isPickupAtStore && isCodOption(value)) {
      onChange("Thu Tiền Ngay");
      setSelectedOption("Thu Tiền Ngay");
    }
  }, [isPickupAtStore, value, readOnly, onChange]);

  const handleEditClick = () => {
    setIsEditing((prev) => !prev);
    setSearchTerm(""); // Reset giá trị tìm kiếm khi mở popover
  };

  const handleOptionChange = (val: string) => {
    // Không cho chọn COD khi nhận tại cửa hàng
    if (isPickupAtStore && isCodOption(val)) {
      NotificationExtension.Warn(
        "Không thể chọn Giao Hàng Thu Tiền(COD) khi nhận tại cửa hàng!"
      );
      return;
    }

    if (disabledOptions?.includes(val)) {
      NotificationExtension.Warn(
        "Phương thức thanh toán này hiện không khả dụng!"
      );
      return;
    }

    // Kiểm tra nếu value chứa từ "Công" và điều kiện không đạt
    if (
      val.includes("Công") &&
      (!customerCheck?.debtTerm || customerCheck.debtTerm === 0)
    ) {
      NotificationExtension.Warn(
        "Chưa chọn khách hàng hoặc khách đã chọn không được cấp công nợ"
      );
      return; // Dừng lại, không thay đổi giá trị
    }

    // Thay đổi giá trị nếu không vi phạm điều kiện
    setSelectedOption(val);
    onChange(val);
    setIsEditing(false);
  };

  const filteredOptions = options?.filter((option) =>
    option?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  return (
    <Flex align="center" gap="xs" w={"350px"}>
      {label && (
        <Text style={{ flex: isCod ? "0 0 10px" : "0 0 100px" }}>
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </Text>
      )}
      <Tooltip
        label={`${selectedOption}`}
        arrowOffset={10}
        arrowSize={4}
        withArrow
        color="blue"
      >
        <Text
          fw={500}
          c={color}
          style={{ whiteSpace: "nowrap", pointerEvents: "auto" }}
          onClick={onClick}
          truncate
        >
          {selectedOption}
        </Text>
      </Tooltip>
      {!readOnly && (
        <Popover
          opened={isEditing}
          onClose={() => setIsEditing(false)}
          width={200}
          position="bottom"
          withArrow
          withinPortal
        >
          <Popover.Target>
            <ActionIcon size={18} variant="white" onClick={handleEditClick}>
              <IconEdit color={cobalt_blue.base} size={18} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown style={{ minWidth: width }}>
            <TextInput
              placeholder="Tìm kiếm..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.currentTarget.value)}
              mb={8}
            />
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              <RadioGroup
                value={selectedOption}
                onChange={handleOptionChange}
                label={`Chọn ${label.toLowerCase()}`}
                required
              >
                {filteredOptions?.map((option, index) => {
                  const isCod = isCodOption(option);
                  const isDisabled =
                    (isPickupAtStore && isCod) ||
                    disabledOptions?.includes(option);

                  return (
                    <Tooltip
                      key={index}
                      label="Không thể chọn giao hàng thu tiền khi nhận tại cửa hàng"
                      disabled={!isDisabled}
                      position="right"
                      withArrow
                      withinPortal
                    >
                      <div
                        style={{
                          marginTop: 5,
                          padding: "2px 6px",
                          borderRadius: 4,
                          ...(isDisabled
                            ? {
                                backgroundColor: "#f1f3f5",
                                cursor: "not-allowed",
                                opacity: 0.6,
                              }
                            : {}),
                        }}
                      >
                        <Radio
                          value={option}
                          label={
                            isDisabled ? (
                              <span
                                style={{
                                  color: "#868e96",
                                  cursor: "not-allowed",
                                }}
                              >
                                {option}{" "}
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "#fa5252",
                                    fontWeight: 400,
                                  }}
                                >
                                  (Không khả dụng)
                                </span>
                              </span>
                            ) : (
                              option
                            )
                          }
                          disabled={isDisabled}
                          style={
                            isDisabled ? { pointerEvents: "none" } : undefined
                          }
                        />
                      </div>
                    </Tooltip>
                  );
                })}
              </RadioGroup>
            </div>
          </Popover.Dropdown>
        </Popover>
      )}
    </Flex>
  );
};

export default SelectPaymentMethod;
